import React, { useState, useEffect, useCallback } from 'react';
import {
    getPools, getParticipants, getOrganizers,
    getPoolEntries, enrollParticipants, deletePoolParticipants, deletePoolEntry,
    sendInvites
} from '../services/api';

function AdminEnrollment({ user = {}, showFeedback, activeTournament }) {
    // is_platform_admin only -- GET /participants is platformAdminOnly on
    // the backend (see comment below); the legacy is_admin flag doesn't
    // grant that access, so including it here caused a doomed fetch
    // attempt and a 403 for anyone with only that flag set.
    const isPlatformAdmin = user.is_platform_admin;

    const [pools,        setPools]        = useState([]);
    const [activePool,   setActivePool]   = useState(null);
    const [participants, setParticipants] = useState([]);
    const [entries,      setEntries]      = useState([]);
    const [selected,     setSelected]     = useState([]);

    // Registration link + bulk invite state. The link is a general
    // registration link (per B in the design discussion: recipients pick
    // their own tournament/pools, same as today's self-service flow) --
    // it's the same for every tournament this organizer runs, so it isn't
    // tied to activeTournament at all.
    const registrationUrl = user.organizer_id
        ? `${window.location.origin}/register?org=${user.organizer_id}`
        : null;
    const [copied,        setCopied]        = useState(false);
    const [inviteEmails,  setInviteEmails]  = useState('');
    const [sendingInvites, setSendingInvites] = useState(false);

    const handleCopyLink = async () => {
        if (!registrationUrl) return;
        try {
            await navigator.clipboard.writeText(registrationUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            showFeedback('Could not copy link -- copy it manually instead', 'error');
        }
    };

    const handleSendInvites = async () => {
        const emails = inviteEmails
            .split(/[\n,]/)
            .map(e => e.trim())
            .filter(e => e.length > 0);

        if (emails.length === 0) {
            showFeedback('Enter at least one email address', 'error');
            return;
        }

        setSendingInvites(true);
        try {
            const res = await sendInvites(emails);
            if (res.data.failed.length === 0) {
                showFeedback(`Invite sent to ${res.data.sent.length} ${res.data.sent.length === 1 ? 'person' : 'people'}`);
                setInviteEmails('');
            } else {
                showFeedback(
                    `Sent ${res.data.sent.length}, failed ${res.data.failed.length}: ${res.data.failed.join(', ')}`,
                    'error'
                );
            }
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to send invites', 'error');
        } finally {
            setSendingInvites(false);
        }
    };

    // Soft warning only -- never blocks sending. The invite is a general
    // link (recipients pick their own tournament/pool), so there's no
    // specific pool to check against at send time; this just gives the
    // organizer a heads-up before they invite more people than their
    // plan's per-pool limit, in case everyone ends up in the same pool.
    const inviteEmailCount = inviteEmails
        .split(/[\n,]/)
        .map(e => e.trim())
        .filter(e => e.length > 0).length;
    const participantLimit = user.plan_limits?.participants_per_pool;
    const showInviteLimitWarning = participantLimit && inviteEmailCount > participantLimit;


    useEffect(() => {
        // GET /participants is platformAdminOnly. Regular organizers can't see
        // the full participant list — they manage enrollment via pool entries.
        if (!isPlatformAdmin) return;
        getParticipants().then(res => setParticipants(res.data));
    }, [isPlatformAdmin]);

    // Platform-admin-only: filter the "All participants" list down to one
    // organizer's own participants, so enrolling someone into THIS
    // organizer's pool doesn't require scanning a single unfiltered,
    // platform-wide list where a same-named or easily-confused participant
    // from a different organizer could get enrolled by mistake. Mirrors
    // the identical pattern already used in AdminParticipants.js.
    const [organizers,          setOrganizers]          = useState([]);
    const [organizerFilter,     setOrganizerFilter]     = useState('all');
    const [filteredByOrganizer, setFilteredByOrganizer] = useState(null);

    useEffect(() => {
        if (!isPlatformAdmin) return;
        getOrganizers().then(res => setOrganizers(res.data)).catch(() => {});
    }, [isPlatformAdmin]);

    const refreshOrganizerFilter = useCallback((orgId) => {
        if (orgId === 'all') {
            setFilteredByOrganizer(null);
            return;
        }
        getParticipants(orgId)
            .then(res => setFilteredByOrganizer(res.data))
            .catch(err => showFeedback(err.response?.data?.error || 'Failed to load participants', 'error'));
    }, [showFeedback]);

    const handleOrganizerFilterChange = (orgId) => {
        setOrganizerFilter(orgId);
        setSelected([]); // clear selection -- previous selection may not even be in the new filtered list
        refreshOrganizerFilter(orgId);
    };

    const activeParticipants = filteredByOrganizer !== null ? filteredByOrganizer : participants;

    useEffect(() => {
        if (!activeTournament) return;
        getPools(activeTournament.id).then(res => {
            setPools(res.data);
            if (res.data.length > 0) setActivePool(res.data[0]);
            else setActivePool(null);
        });
    }, [activeTournament]);

    useEffect(() => {
        if (!activePool) return;
        getPoolEntries(activePool.id).then(res => setEntries(res.data));
    }, [activePool]);

    const enrolledIds = entries.map(e => e.participant_id);

    const handleToggle = (id) => {
        setSelected(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        const unenrolled = activeParticipants.filter(p => !enrolledIds.includes(p.id)).map(p => p.id);
        setSelected(unenrolled);
    };

    const handleEnroll = async () => {
        if (selected.length === 0) {
            showFeedback('No participants selected', 'error');
            return;
        }
        if (!activePool) {
            showFeedback('No pool selected', 'error');
            return;
        }
        try {
            const res = await enrollParticipants(activePool.id, selected);
            showFeedback(`Enrolled ${res.data.enrolled} participants`);
            setSelected([]);
            getPoolEntries(activePool.id).then(res => setEntries(res.data));
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to enroll', 'error');
        }
    };

    const handleRemoveOne = async (entry) => {
        if (!window.confirm(`Remove ${entry.participant} from "${activePool?.name}"?`)) return;
        try {
            await deletePoolEntry(activePool.id, entry.entry_id);
            showFeedback(`${entry.participant} removed from pool`);
            getPoolEntries(activePool.id).then(res => setEntries(res.data));
        } catch (err) {
            showFeedback('Failed to remove participant', 'error');
        }
    };

    const handleRemoveAll = async () => {
        if (!window.confirm(`Remove ALL participants from "${activePool?.name}"?`)) return;
        try {
            await deletePoolParticipants(activePool.id);
            showFeedback('All participants removed');
            getPoolEntries(activePool.id).then(res => setEntries(res.data));
        } catch (err) {
            showFeedback('Failed to remove participants', 'error');
        }
    };

    return (
        <div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-black text-white mb-1">Invite participants</h3>
                <p className="text-gray-500 text-sm mb-4">
                    Share your registration link, or send it directly to specific people.
                    Either way, they choose their own tournament and pool(s) when they register,
                    and your approval is still required before they're enrolled.
                </p>

                {registrationUrl ? (
                    <div className="flex items-center gap-2 mb-5">
                        <input
                            readOnly
                            value={registrationUrl}
                            onClick={e => e.target.select()}
                            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm font-mono focus:outline-none"
                        />
                        <button onClick={handleCopyLink}
                            className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap">
                            {copied ? 'Copied!' : 'Copy link'}
                        </button>
                    </div>
                ) : (
                    <p className="text-gray-600 text-sm mb-5">
                        You need an organizer account to get a registration link.
                    </p>
                )}

                <div className="flex flex-col gap-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Or email invites directly (one per line, or comma-separated)
                    </span>
                    <textarea
                        value={inviteEmails}
                        onChange={e => setInviteEmails(e.target.value)}
                        rows={3}
                        placeholder={"jane@example.com\njohn@example.com"}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-orange-500"
                    />
                    {showInviteLimitWarning && (
                        <p className="text-yellow-400 text-xs">
                            Your plan allows up to {participantLimit} participants per pool — you're
                            about to invite {inviteEmailCount}. If they all join the same pool, some
                            may not be approvable once that pool is full.
                        </p>
                    )}
                    <button onClick={handleSendInvites} disabled={sendingInvites || !inviteEmails.trim()}
                        className="self-start bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-5 py-2 rounded-lg transition-colors text-sm">
                        {sendingInvites ? 'Sending…' : 'Send invites'}
                    </button>
                </div>
            </div>

            <div className="flex gap-3 mb-6 flex-wrap items-end">
                {isPlatformAdmin && (
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Organizer</span>
                        <select value={organizerFilter} onChange={e => handleOrganizerFilterChange(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                            <option value="all">All organizers</option>
                            {organizers.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
                        </select>
                    </div>
                )}
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Pool</span>
                    <select value={activePool?.id || ''}
                        onChange={e => setActivePool(pools.find(p => p.id === parseInt(e.target.value)))}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        {pools.length === 0 && <option value="">No pools</option>}
                        {pools.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-white">All participants</h3>
                        <button onClick={handleSelectAll}
                            className="text-xs text-orange-400 hover:text-orange-300 transition-colors">
                            Select unenrolled
                        </button>
                    </div>
                    <div className="space-y-2 mb-4">
                        {activeParticipants.map(p => {
                            const isEnrolled = enrolledIds.includes(p.id);
                            const isSelected = selected.includes(p.id);
                            return (
                                <div key={p.id}
                                    onClick={() => !isEnrolled && handleToggle(p.id)}
                                    className={'flex items-center justify-between p-3 rounded-lg border transition-colors ' +
                                        (isEnrolled
                                            ? 'border-gray-700 bg-gray-800 opacity-50 cursor-not-allowed'
                                            : isSelected
                                                ? 'border-orange-500 bg-gray-800 cursor-pointer'
                                                : 'border-gray-800 hover:border-gray-600 cursor-pointer')}>
                                    <div>
                                        <p className="text-white text-sm font-medium">{p.name}</p>
                                        <p className="text-gray-500 text-xs">{p.email}</p>
                                        {isPlatformAdmin && organizerFilter === 'all' && p.organizer_name && (
                                            <p className="text-gray-600 text-xs mt-0.5">Organizer: {p.organizer_name}</p>
                                        )}
                                    </div>
                                    {isEnrolled
                                        ? <span className="text-xs text-green-400">Enrolled</span>
                                        : isSelected
                                            ? <span className="text-xs text-orange-400">Selected</span>
                                            : <span className="text-xs text-gray-600">Not enrolled</span>
                                    }
                                </div>
                            );
                        })}
                    </div>
                    <button onClick={handleEnroll} disabled={selected.length === 0}
                        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold py-2 rounded-lg transition-colors text-sm">
                        Enroll {selected.length > 0 ? `${selected.length} participants` : 'selected'}
                    </button>
                </div>

                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-white">
                            Enrolled in {activePool?.name || 'no pool selected'}
                            <span className="ml-2 text-sm font-normal text-gray-500">({entries.length})</span>
                        </h3>
                        {entries.length > 0 && (
                            <button onClick={handleRemoveAll}
                                className="text-xs text-red-400 hover:text-red-300 transition-colors">
                                Remove all
                            </button>
                        )}
                    </div>
                    {entries.length === 0 ? (
                        <p className="text-gray-500 text-sm">No participants enrolled yet.</p>
                    ) : (
                        <div className="space-y-2">
                            {entries.map(e => (
                                <div key={e.entry_id} className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-gray-800">
                                    <div>
                                        <p className="text-white text-sm font-medium">{e.participant}</p>
                                        <p className="text-gray-500 text-xs">{e.email}</p>
                                    </div>
                                    <button onClick={() => handleRemoveOne(e)}
                                        className="text-xs text-red-400 hover:text-red-300 transition-colors px-2 py-1 rounded">
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminEnrollment;